import { getTheme, IconButton, Stack, Text } from '@fluentui/react';
import { ConfigOptions } from '@ivicos/react-jitsi/dist/types';
import CallActionBar from 'features/Rooms/RoomView/comps/RoomCall/CallActionBar/CallActionBar';
import SpeakerStats from 'features/Rooms/SpeakerStats/SpeakerStats';
import { IvicosColors } from 'kits/colorKit';
import React, { useState } from 'react';
import { isSafari } from 'react-device-detect';
import { useShowAttachmentPanel } from 'shared-state/display-options/hooks';
import AddOn from '../comps/AddOnView/AddOn';
import AttachmentsPanel from '../comps/Attachments/AttachmentPanel/AttachmentsPanel';
import CallSurface from '../comps/RoomCall/CallSurface/CallSurface';
import FirefoxAlert from '../FirefoxAlert/FirefoxAlert';
import { DeviceSettingsAtVisitorEntrance } from '../DeviceSettingsAtVisitorEntrance/DeviceSettingsAtVisitorEntrance';
import { useIsVisitorAtLandingPage } from 'shared-state/location/hook';
import { IRoomCallControllerViewProps } from './types';
import RenderJitsiOrWidgets from './components/RenderJitsiOrWidgets';
import { createCallActionHandler } from './components/callActionHandlers';
import DraggableJitsiContainer from './components/DraggableJitsiContainer';
import EtherpadAlert from './components/EtherpadAlert';

const RoomCallControllerView: React.FC<IRoomCallControllerViewProps> = (props) => {
    const {
        addOnIsVisible,
        translateToHideOrShow,
        jitsiShowHideInnerContainerOpacity,
        isHidden,
        encryptionIsEnabled,
        jitsiAPI,
        executeCommand,
        actionBarActions,
        isConnected,
        micIsMuted,
        cameraIsMuted,
        activeAddOnData,
        selectedAttachment,
        setSelectedAttachment,
        jitsiConfig,
        interfaceConfig,
        isVideoHidden,
        setIsVideoHidden,
        statsAreVisible,
        stats,
        room,
        displayName,
        jwt,
        roomName,
        domain,
        isLocalUserHandRaised,
        userIsAloneInOwnPersonalRoom,
        profileImageURL,
        sharingIsOn,
        handleJitsiMeetExternalAPILoad,
        userEmailAddress,
        isChatVisible,
        shouldShowFireFoxAlert,
        startingPosition,
        onDragStartHandler,
        onDragEndHandler
    } = props;

    const theme = getTheme();
    const shouldRenderJitsi = !(userIsAloneInOwnPersonalRoom && cameraIsMuted && micIsMuted && !sharingIsOn);

    const [triggerJitsiRender, setTriggerJitsiRender] = useState<boolean>(shouldRenderJitsi);
    const [showAttachmentPanel] = useShowAttachmentPanel();
    const [etherpadAlert, setEtherpadAlert] = useState<boolean>(true);
    const [newJitsiConfig, setNewJitsiConfig] = useState<ConfigOptions>({ ...jitsiConfig, disableDeepLinking: true });
    const [roundTableBorder, setRoundTableBorder] = useState<string>('');
    const [isVisitorAtLandingPage] = useIsVisitorAtLandingPage();

    const onHandRaiseDismiss = () => {
        executeCommand('toggleRaiseHand');
    };

    const attachmentPanelComponent = <AttachmentsPanel jitsiAPI={jitsiAPI} />;

    const handleActionPressed = createCallActionHandler({
        shouldRenderJitsi,
        triggerJitsiRender,
        cameraIsMuted,
        micIsMuted,
        jitsiConfig,
        setTriggerJitsiRender,
        setNewJitsiConfig,
        executeCommand
    });

    return (
        <CallSurface isHidden={isHidden} borderColor={encryptionIsEnabled ? IvicosColors.Blau : undefined}>
            {isVisitorAtLandingPage && <DeviceSettingsAtVisitorEntrance jitsiAPI={jitsiAPI}></DeviceSettingsAtVisitorEntrance>}

            <CallActionBar
                micIsMuted={micIsMuted}
                cameraIsMuted={cameraIsMuted}
                sharingIsOn={sharingIsOn}
                statsAreVisible={statsAreVisible}
                isLocalUserHandRaised={isLocalUserHandRaised}
                isChatVisible={isChatVisible}
                visible={true}
                actions={actionBarActions}
                onActionPressed={handleActionPressed}
            />

            {shouldShowFireFoxAlert ? (
                <Stack style={{ position: 'absolute', top: '65px', left: '8px' }}>
                    <FirefoxAlert></FirefoxAlert>
                </Stack>
            ) : null}

            {activeAddOnData && (
                <Stack horizontalAlign="space-between" verticalAlign="center" horizontal style={{ padding: 8, background: theme.palette.themeSecondary }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <img src={activeAddOnData.attributes.iconUrl} height={24} />
                        <Text style={{ color: theme.palette.white }}>{activeAddOnData.attributes.displayName}</Text>
                    </Stack>
                    <IconButton
                        styles={{ root: { color: theme.palette.white } }}
                        onClick={() => {
                            setSelectedAttachment(undefined);
                        }}
                        iconProps={{ iconName: 'clear' }}
                    />
                </Stack>
            )}

            <Stack
                grow
                horizontal
                verticalAlign="stretch"
                style={{
                    border: roundTableBorder,
                    height: '100%'
                }}
            >
                {true && jwt && roomName && domain && (
                    <>
                        {activeAddOnData && (
                            <Stack grow style={{ border: '6px ' + theme.palette.themeSecondary + ' solid' }}>
                                {isSafari && activeAddOnData?.id == 'etherpad' && etherpadAlert ? (
                                    <EtherpadAlert onClose={() => setEtherpadAlert(false)} theme={theme} />
                                ) : (
                                    <AddOn isHidden={!addOnIsVisible} addOn={activeAddOnData} attachmentRef={selectedAttachment?.ref} />
                                )}
                            </Stack>
                        )}

                        <DraggableJitsiContainer
                            addOnIsVisible={addOnIsVisible}
                            startingPosition={startingPosition}
                            translateToHideOrShow={translateToHideOrShow}
                            isSafari={isSafari}
                            theme={theme}
                            isVideoHidden={isVideoHidden}
                            isConnected={isConnected}
                            encryptionIsEnabled={encryptionIsEnabled}
                            cameraIsMuted={cameraIsMuted}
                            micIsMuted={micIsMuted}
                            jitsiShowHideInnerContainerOpacity={jitsiShowHideInnerContainerOpacity}
                            onDragStartHandler={onDragStartHandler}
                            onDragEndHandler={onDragEndHandler}
                            setIsVideoHidden={setIsVideoHidden}
                        >
                            <RenderJitsiOrWidgets
                                shouldRenderJitsi={shouldRenderJitsi}
                                triggerJitsiRender={triggerJitsiRender}
                                userIsAloneInOwnPersonalRoom={userIsAloneInOwnPersonalRoom ?? false}
                                domain={domain}
                                roomName={roomName}
                                jitsiConfig={newJitsiConfig}
                                interfaceConfig={interfaceConfig}
                                jwt={jwt}
                                displayName={displayName}
                                userEmailAddress={userEmailAddress}
                                addOnIsVisible={addOnIsVisible}
                                handleJitsiMeetExternalAPILoad={handleJitsiMeetExternalAPILoad}
                                profileImageURL={profileImageURL}
                            />
                        </DraggableJitsiContainer>
                    </>
                )}
                {room && showAttachmentPanel !== 'closed' && attachmentPanelComponent}

                {room && (
                    <SpeakerStats
                        statsAreVisible={statsAreVisible}
                        stats={stats}
                        executeCommand={executeCommand}
                        roundTableBorder={roundTableBorder}
                        setRoundTableBorder={setRoundTableBorder}
                        isLocalUserHandRaised={isLocalUserHandRaised}
                        onHandRaiseDismiss={onHandRaiseDismiss}
                        room={room}
                    />
                )}
            </Stack>
        </CallSurface>
    );
};

export default RoomCallControllerView;
